/**
 * Green Flag — MatchFillWidget
 *
 * Top-of-Events-page widget prompting the user to submit their matches from
 * a past event. Shows a stack of profile cards (undecided dating-category
 * `participants` from a PastSparkForUser). Two circular buttons sit over the
 * bottom edge:
 *
 *   - heart-broken (danger) → "pass" / no spark
 *   - heart        (brand)  → "match" / spark
 *
 * Each decision fires a PUT to `/user/relationship` with
 * `UserRelationshipType.Spark` (match) or `.NoSpark` (pass). Swiping the top
 * card left/right also records a decision.
 *
 * After a decision, the top card flies off and we navigate to
 * `/event/:sparkId/match` for the full matching flow. Tapping the top photo (no
 * swipe) navigates immediately with the current top as focus.
 */

import { useRef, useState } from "react";
import { Box, Button, chakra, useSlotRecipe } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  SparkParticipantStatus,
  UserRelationshipType,
  type UserProfile,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../lib/apiReq";
import { store, useStoreData } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import { MediaBlobImage, primaryProfileImagePath } from "../../lib/profileMedia";
import {
  clearShouldPromptDoneMatching,
  shouldPromptDoneMatching,
} from "../../lib/donePromptFlags";
import { Icon } from "../Icon/Icon";
import { isProfileCompleteForMatching } from "../CompleteProfileGate/CompleteProfileGate";

export interface MatchFillEvent {
  title: string;
  activity: string;
  starts_at: string;
  primary_image?: string;
  hero_image?: string;
}

const MChakraDiv = motion.create(chakra.div);

export type MatchDecision = "match" | "pass";

export interface MatchFillWidgetProps {
  sparkId: number;
  candidates: UserProfile[];
  /**
   * Dating or friendship, matching the category of the candidates passed in.
   * Drives the decision buttons, so a Friends Only viewer is never asked to
   * add a friend by tapping a heart. Defaults to dating.
   */
  category?: "dating" | "friend";
  event?: MatchFillEvent;
  onDecision?: (profile: UserProfile, decision: MatchDecision) => void;
}

const VISIBLE = 3;
const PEEK_OFFSET = 14;
const PEEK_SCALE = 0.05;
const PEEK_ROTATE = 3;

export function MatchFillWidget({
  sparkId,
  candidates,
  category = "dating",
  event,
  onDecision,
}: MatchFillWidgetProps) {
  const isFriend = category === "friend";
  const recipe = useSlotRecipe({ key: "matchFillWidget" });
  const styles = recipe({});
  const navigate = useNavigate();
  const storeData = useStoreData();
  const [relationshipReq] = useReq<{
    userId: number;
    sparkId: number | null;
    status: SparkParticipantStatus | null;
  }>(apiReq);
  const [matchesCompletedReq] = useReq<{ success: true }>(apiReq);
  // Re-render trigger. We read `shouldPromptDoneMatching(sparkId)` from
  // sessionStorage directly at render time so grid re-entries (which flip
  // the flag back on) always win over a stale prop snapshot, and so the
  // "Not yet" handler below can force an immediate re-render after clearing.
  const [, bumpTick] = useState(0);
  const showDonePrompt = shouldPromptDoneMatching(sparkId);

  async function confirmMatchesCompleted() {
    const response = await matchesCompletedReq.post(
      `/spark/${sparkId}/matches-completed`,
      {}
    );
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateMatchesCompleted,
        params: { sparkId },
      });
    }
  }

  function dismissDonePrompt() {
    clearShouldPromptDoneMatching(sparkId);
    bumpTick((n) => n + 1);
  }

  const [exitDirection, setExitDirection] = useState<"left" | "right" | null>(
    null
  );
  // Event chip expansion: hover expands, click toggles. A click that closes
  // while hovering must keep the chip closed until the pointer leaves —
  // otherwise hover would immediately re-open it.
  const [chipPinnedOpen, setChipPinnedOpen] = useState(false);
  const [chipHovering, setChipHovering] = useState(false);
  const [chipSuppressHover, setChipSuppressHover] = useState(false);
  const eventChipExpanded =
    chipPinnedOpen || (chipHovering && !chipSuppressHover);
  // While a card is flying off, freeze the rendered stack. Otherwise a
  // mid-swipe store update (the server's relationship response mutates
  // `pastSparks`, which re-filters the parent's `candidates`) shifts the
  // remaining cards into new stack positions and they animate along with
  // the leaving card.
  const frozenCandidatesRef = useRef<UserProfile[] | null>(null);
  const displayCandidates =
    exitDirection !== null && frozenCandidatesRef.current
      ? frozenCandidatesRef.current
      : candidates;

  const topProfile = displayCandidates[0];
  const nextProfile = displayCandidates[1] ?? null;

  function openMatchPage(
    focusProfile: UserProfile | null,
    pendingDecision?: MatchDecision
  ) {
    navigate(`/event/${sparkId}/match`, {
      state: {
        focusUserId: focusProfile?.id,
        animate: true,
        pendingDecision: pendingDecision
          ? { userId: focusProfile?.id, decision: pendingDecision }
          : undefined,
      },
    });
  }

  // Holds the status returned from the relationship PUT while the swipe
  // animation plays. Dispatched to the store in handleExitComplete so the
  // parent provider doesn't drop this widget mid-swipe — if candidates empty
  // out during the animation, the widget unmounts before onAnimationComplete
  // fires and we never navigate to the Match page.
  const pendingStatusRef = useRef<{
    userId: number;
    status: SparkParticipantStatus | null;
  } | null>(null);

  function handleDecision(decision: MatchDecision) {
    if (!topProfile) return;
    // If the user can't actually record a relationship yet — because their
    // own profile is incomplete — don't animate the swipe or hit the API.
    // Just send them to the match page so the CompleteProfileGate catches
    // them there. Gate lives on Match's route, not here, so we can't render
    // it over the widget.
    if (!isProfileCompleteForMatching(storeData.user)) {
      openMatchPage(topProfile, decision);
      return;
    }
    frozenCandidatesRef.current = displayCandidates;
    onDecision?.(topProfile, decision);
    const decidedUserId = topProfile.id;
    void relationshipReq
      .put("/user/relationship", {
        userId: decidedUserId,
        sparkId,
        relationship:
          decision === "match"
            ? UserRelationshipType.Spark
            : UserRelationshipType.NoSpark,
      })
      .then((response) => {
        if (!response.success) return;
        pendingStatusRef.current = {
          userId: decidedUserId,
          status: response.result.status,
        };
      });
    setExitDirection(decision === "match" ? "right" : "left");
    // openMatchPage fires from onAnimationComplete once the swipe lands.
  }

  function handleExitComplete() {
    setExitDirection(null);
    frozenCandidatesRef.current = null;
    const pending = pendingStatusRef.current;
    pendingStatusRef.current = null;
    if (pending) {
      store.dispatch({
        type: ActionType.UpdateParticipantStatus,
        params: { sparkId, userId: pending.userId, status: pending.status },
      });
    }
    openMatchPage(nextProfile ?? topProfile);
  }

  if (!topProfile) return null;

  return (
    <chakra.div css={styles.root}>
      <Box css={styles.stack}>
        {displayCandidates.slice(0, showDonePrompt ? 1 : VISIBLE).map((profile, i) => {
          const isTop = i === 0;
          const depth = i;
          const baseRotate = isTop
            ? 0
            : (depth % 2 === 0 ? 1 : -1) * PEEK_ROTATE * depth;
          const baseScale = 1 - depth * PEEK_SCALE;
          const baseY = depth * PEEK_OFFSET;
          const leaving = isTop && exitDirection !== null;

          return (
            <MChakraDiv
              key={profile.id}
              css={styles.card}
              style={{ zIndex: VISIBLE - depth }}
              initial={false}
              animate={
                leaving
                  ? {
                      x: exitDirection === "right" ? 480 : -480,
                      rotate: exitDirection === "right" ? 22 : -22,
                    }
                  : {
                      x: 0,
                      y: baseY,
                      rotate: baseRotate,
                      scale: baseScale,
                      opacity: 1,
                    }
              }
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 28,
              }}
              onClick={
                isTop && !leaving ? () => openMatchPage(profile) : undefined
              }
              onAnimationComplete={(definition) => {
                if (!leaving) return;
                const target = definition as { x?: number };
                if (typeof target?.x === "number" && Math.abs(target.x) > 100) {
                  handleExitComplete();
                }
              }}
            >
              <MediaBlobImage
                path={primaryProfileImagePath(profile)}
                alt={profile.firstName ?? "Profile"}
                css={styles.image}
                fallback={<Icon name="user-solid" boxSize="35%" />}
              />

              {isTop && event && (
                <Box
                  as="button"
                  css={styles.eventChip}
                  data-expanded={eventChipExpanded ? "true" : undefined}
                  aria-expanded={eventChipExpanded}
                  aria-label={
                    eventChipExpanded ? "Hide event details" : "Show event details"
                  }
                  onMouseEnter={() => setChipHovering(true)}
                  onMouseLeave={() => {
                    setChipHovering(false);
                    setChipSuppressHover(false);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (eventChipExpanded) {
                      setChipPinnedOpen(false);
                      if (chipHovering) setChipSuppressHover(true);
                    } else {
                      setChipPinnedOpen(true);
                      setChipSuppressHover(false);
                    }
                  }}
                >
                  {(event.primary_image || event.hero_image) && (
                    <Box
                      css={styles.eventChipThumb}
                      style={{
                        backgroundImage: `url(${event.primary_image || event.hero_image})`,
                      }}
                    />
                  )}
                  {eventChipExpanded && (
                    <Box css={styles.eventChipText}>
                      <Box css={styles.eventChipTitle}>
                        {event.title || event.activity}
                      </Box>
                      <Box css={styles.eventChipDate}>
                        {dayjs(event.starts_at).format("dddd, MMM D")}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </MChakraDiv>
          );
        })}

        {showDonePrompt && (
          <Box css={styles.donePrompt}>
            <Box css={styles.donePromptTitle}>Are you done matching?</Box>
            <Box css={styles.donePromptActions}>
              <Button
                size="lg"
                variant="outline"
                colorPalette="gray"
                color="white"
                borderColor="whiteAlpha.700"
                _hover={{ bg: "whiteAlpha.200" }}
                onClick={dismissDonePrompt}
              >
                No
              </Button>
              <Button
                size="lg"
                onClick={() => {
                  void confirmMatchesCompleted();
                }}
              >
                Yes
              </Button>
            </Box>
          </Box>
        )}

        {!showDonePrompt && (
          <Box css={styles.actions}>
            <chakra.button
              css={styles.actionButton}
              color="fg.danger"
              aria-label={isFriend ? "Not a friend" : "Pass"}
              onClick={(e) => {
                e.stopPropagation();
                handleDecision("pass");
              }}
            >
              {/* A broken heart reads as romantic rejection — the wrong frame
                  for declining a friendship. */}
              <Icon name={isFriend ? "x" : "heart-broken"} boxSize="22px" />
            </chakra.button>
            <chakra.button
              css={styles.actionButton}
              color={isFriend ? "fg.friends" : "fg.brand"}
              aria-label={isFriend ? "Add friend" : "Match"}
              onClick={(e) => {
                e.stopPropagation();
                handleDecision("match");
              }}
            >
              <Icon name={isFriend ? "smile" : "heart"} boxSize="22px" />
            </chakra.button>
          </Box>
        )}
      </Box>
    </chakra.div>
  );
}
